import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';

import { queryClient } from '@/adapters/query';
import {
  SCREENER_SETTINGS_BATCH_KEY,
  SCREENER_SETTINGS_PER_FACILITY_KEY
} from '@/hooks/screener-settings/useFacilityScreenerSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  selectedFacilityIdAtom,
  useFacilitiesQuery,
  useSelectedFacilityId
} from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { RoutePath } from '@/routes/RoutePath';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';
import { atomWithSession } from '@/utils/sessionStorageAtom';

export const ALL_FACILITIES_PARAM = 'all_facilities';
export const FACILITY_TYPE_PARAM = 'facility_type';
export const SET_FACILITY_ID_PARAM = 'set_facility_id';

// Adding a route here only flips on the all-facilities view derivation. The
// page also needs: data queries that handle a missing facility id, and UI that
// can render rows from multiple facilities (e.g. a facility column).
export const SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT: RoutePath[] = [
  RoutePath.CRMScreenerAllLeads,
  RoutePath.CRMScreenerESign,
  RoutePath.CRMTasks,
  RoutePath.CRMScreenerEfax,
  RoutePath.CRMScreenerPalAuthorizations,
  RoutePath.CRMScreenerPalReauths,
  RoutePath.CRMReferralOutbound
];

export type ScreenerFacilityView = {
  kind: 'specific' | 'all-facilities';
  facilityType: FacilityFacilityTypeEnum;
};

interface UseScreenerFacilityViewResult {
  view: ScreenerFacilityView;
  selectedFacilityId: string | null;
  setFacilityId: (facilityId: string) => void;
}

export const allFacilitiesIntentAtom = atomWithSession<{
  facilityType: FacilityFacilityTypeEnum;
} | null>('screenerAllFacilitiesIntent', null);

export const isValidFacilityType = (
  value: unknown
): value is FacilityFacilityTypeEnum => {
  return (
    typeof value === 'string' &&
    Object.values(FacilityFacilityTypeEnum).includes(
      value as FacilityFacilityTypeEnum
    )
  );
};

export const useUserCanUseAllFacilities = (): boolean => {
  const currentUser = useCurrentUser().data;
  return (
    !!currentUser && (currentUser.user_facility_accesses?.length ?? 0) >= 2
  );
};

const isOnAllFacilitiesSupportedPath = (pathname: string) =>
  SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT.includes(pathname as RoutePath);

/**
 * Read-only access to the screener facility view + a setter that updates the
 * selected facility (without touching intent or URL). The atom is the source
 * of truth for all-facilities mode; URL is a derived side-effect synced by
 * `useFacilitySelectorController`.
 */
export const useScreenerFacilityView = (): UseScreenerFacilityViewResult => {
  const location = useLocation();
  const pathname = location.pathname;

  const selectedFacilityId = useSelectedFacilityId();
  const [, setSelectedFacilityIdAtom] = useAtom(selectedFacilityIdAtom);
  const intent = useAtomValue(allFacilitiesIntentAtom);
  const userCanUseAllFacilities = useUserCanUseAllFacilities();
  const isMobileViewport = useIsMobileViewport();

  const { data: facilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();

  const specificFacilityType =
    facilitiesResponse?.facilities.find((f) => f.id === selectedFacilityId)
      ?.facility_type ?? FacilityFacilityTypeEnum.SkilledNursing;

  const view = useMemo<ScreenerFacilityView>(() => {
    if (
      !intent ||
      !isValidFacilityType(intent.facilityType) ||
      !userCanUseAllFacilities ||
      !isOnAllFacilitiesSupportedPath(pathname) ||
      isMobileViewport
    ) {
      return { kind: 'specific', facilityType: specificFacilityType };
    }
    return { kind: 'all-facilities', facilityType: intent.facilityType };
  }, [
    intent,
    pathname,
    userCanUseAllFacilities,
    isMobileViewport,
    specificFacilityType
  ]);

  const setFacilityId = useCallback(
    (facilityId: string) => {
      if (facilityId === selectedFacilityId) {
        return;
      }
      setSelectedFacilityIdAtom(facilityId);
      queryClient.getQueriesData([]).forEach(([queryKey]) => {
        const key0 = Array.isArray(queryKey) ? queryKey[0] : queryKey;
        if (
          key0 !== 'useCurrentUser' &&
          key0 !== 'screenerFacilitySettings' &&
          key0 !== SCREENER_SETTINGS_BATCH_KEY &&
          key0 !== SCREENER_SETTINGS_PER_FACILITY_KEY
        ) {
          queryClient.resetQueries(queryKey);
        }
      });
    },
    [selectedFacilityId, setSelectedFacilityIdAtom]
  );

  return {
    view,
    selectedFacilityId,
    setFacilityId
  };
};
